import { useState, useEffect } from 'react';
import { FaSearch, FaMapMarkerAlt, FaLocationArrow } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { geocodeAddress, reverseGeocode } from '../utils/geocoder';
import '../styles/RoutePlanModal.css';

const RoutePlanModal = ({ isOpen, onClose, onStart, onEndpointChange, userLocation, initialOrigin, initialDest }) => {
  const { t } = useTranslation();
  const currentLocationLabel = t('route_plan_modal.current_location');

  const [fromValue, setFromValue] = useState('');
  const [toValue, setToValue] = useState('');
  const [resolvedOrigin, setResolvedOrigin] = useState(null);
  const [resolvedDest, setResolvedDest] = useState(null);
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeError, setGeocodeError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setFromValue(initialOrigin?.label ?? '');
      setToValue(initialDest?.label ?? '');
      setResolvedOrigin(initialOrigin ?? null);
      setResolvedDest(initialDest ?? null);
      setGeocoding(false);
      setGeocodeError(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleUseCurrentLocation = async () => {
    if (!userLocation) return;
    setGeocoding(true);
    const label = (await reverseGeocode(userLocation.lat, userLocation.lng)) ?? currentLocationLabel;
    const origin = { ...userLocation, label };
    setFromValue(label);
    setResolvedOrigin(origin);
    setGeocodeError(null);
    setGeocoding(false);
    if (onEndpointChange) {
      const dest = resolvedDest ?? null;
      if (dest) onEndpointChange({ origin, destination: dest });
    }
  };

  const handleFromKeyDown = async (e) => {
    if (e.key !== 'Enter' || !fromValue.trim()) return;
    e.preventDefault();
    setGeocoding(true);
    setGeocodeError(null);
    try {
      const coords = await geocodeAddress(fromValue.trim());
      const origin = { ...coords, label: fromValue.trim() };
      setResolvedOrigin(origin);
      const dest = resolvedDest ?? null;
      if (onEndpointChange && dest) onEndpointChange({ origin, destination: dest });
    } catch {
      setGeocodeError(t('route_plan_modal.geocode_error'));
    } finally {
      setGeocoding(false);
    }
  };

  const handleToKeyDown = async (e) => {
    if (e.key !== 'Enter' || !toValue.trim()) return;
    e.preventDefault();
    setGeocoding(true);
    setGeocodeError(null);
    try {
      const coords = await geocodeAddress(toValue.trim());
      const destination = { ...coords, label: toValue.trim() };
      setResolvedDest(destination);
      const origin = resolvedOrigin ?? null;
      if (onEndpointChange && origin) onEndpointChange({ origin, destination });
    } catch {
      setGeocodeError(t('route_plan_modal.geocode_error'));
    } finally {
      setGeocoding(false);
    }
  };

  const handleStart = async () => {
    setGeocoding(true);
    setGeocodeError(null);

    try {
      let origin = resolvedOrigin;
      let destination = resolvedDest;

      if (!origin && fromValue.trim()) {
        const coords = await geocodeAddress(fromValue.trim());
        origin = { ...coords, label: fromValue.trim() };
        setResolvedOrigin(origin);
      }
      if (!destination && toValue.trim()) {
        const coords = await geocodeAddress(toValue.trim());
        destination = { ...coords, label: toValue.trim() };
        setResolvedDest(destination);
      }

      if (!origin || !destination) {
        setGeocodeError(t('route_plan_modal.geocode_error'));
        return;
      }

      onClose();
      onStart({ origin, destination });
    } catch {
      setGeocodeError(t('route_plan_modal.geocode_error'));
    } finally {
      setGeocoding(false);
    }
  };

  const isStartDisabled =
    geocoding ||
    (!resolvedOrigin && !fromValue.trim()) ||
    (!resolvedDest && !toValue.trim());

  if (!isOpen) return null;

  return (
    <div className="route-plan-overlay" onClick={onClose}>
      <div className="route-plan-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="route-plan-handle" />
        <h2 className="route-plan-title">{t('route_plan_modal.title')}</h2>
        <p className="route-plan-subtitle">{t('route_plan_modal.subtitle')}</p>

        <div className="route-plan-input-wrapper route-plan-input-from">
          <FaSearch className="route-plan-input-icon" />
          <input
            className="route-plan-input"
            type="text"
            placeholder={t('route_plan_modal.from_placeholder')}
            value={fromValue}
            onChange={(e) => {
              setFromValue(e.target.value);
              setResolvedOrigin(null);
              setGeocodeError(null);
            }}
            onKeyDown={handleFromKeyDown}
          />
          <button
            className="route-plan-location-btn"
            onClick={handleUseCurrentLocation}
            disabled={!userLocation}
            title={t('route_plan_modal.use_current_location')}
          >
            <FaLocationArrow />
          </button>
        </div>

        <div className="route-plan-input-wrapper route-plan-input-to">
          <FaMapMarkerAlt className="route-plan-input-icon" />
          <input
            className="route-plan-input"
            type="text"
            placeholder={t('route_plan_modal.to_placeholder')}
            value={toValue}
            onChange={(e) => {
              setToValue(e.target.value);
              setResolvedDest(null);
              setGeocodeError(null);
            }}
            onKeyDown={handleToKeyDown}
          />
          <button
            className="route-plan-location-btn"
            onClick={async () => {
              if (!userLocation) return;
              setGeocoding(true);
              const label = (await reverseGeocode(userLocation.lat, userLocation.lng)) ?? currentLocationLabel;
              const dest = { ...userLocation, label };
              setToValue(label);
              setResolvedDest(dest);
              setGeocodeError(null);
              setGeocoding(false);
              const origin = resolvedOrigin ?? null;
              if (onEndpointChange && origin) onEndpointChange({ origin, destination: dest });
            }}
            disabled={!userLocation}
            title={t('route_plan_modal.use_current_location')}
          >
            <FaLocationArrow />
          </button>
        </div>

        {geocodeError && (
          <p className="route-plan-error">{geocodeError}</p>
        )}

        <div className="route-plan-actions">
          <button
            className="route-plan-btn-start"
            onClick={handleStart}
            disabled={isStartDisabled}
          >
            {geocoding ? t('route_plan_modal.geocoding') : t('route_plan_modal.start')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoutePlanModal;
